import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createPanelBasketCheckout } from "../../../utils/panelPurchase";
import ImmediateSupplyConsentCheckbox from "./ImmediateSupplyConsentCheckbox";
import {
  addToPanelBasket,
  clearPanelBasket,
  panelBasketCurrency,
  panelBasketTotal,
  readPanelBasket,
  removeFromPanelBasket,
} from "../../../utils/panelBasket";
import useStudentPortalMode, { setDashboardView } from "../../../hooks/useStudentPortalMode";

// A mobile plan covers the whole mock exam and past paper library.
const PLAN_COVERED_TYPES = ["mock_exam", "paper"];

/**
 * Pick several items across the catalogue, then let the parent authorise the lot
 * in one step and pay once.
 *
 * This replaced a per-row "Buy" that opened its own confirm-and-pay dialog: buying
 * three papers meant three card entries and three parent confirmations. The parent
 * step is unchanged in substance — the same single "I am the parent or guardian"
 * tick — it just now covers the whole order, with every line and the total above it.
 *
 * Returns the bar and dialog as one node so a page only has to render {basketUi}.
 */
export function usePanelBasket() {
  const [items, setItems] = useState(readPanelBasket);
  const [open, setOpen] = useState(false);
  const [parentConfirmed, setParentConfirmed] = useState(false);
  const [supplyConsent, setSupplyConsent] = useState(false);
  const [checkout, setCheckout] = useState(null); // { clientSecret, stripePromise }
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Course side of a student who also has a live mobile plan: the item is already theirs on
  // the Mobile side, so it is not bought again — they are sent to the Mobile side instead.
  const { canToggleDashboard, dashboardView, catalogueLocked } = useStudentPortalMode();
  const [planNotice, setPlanNotice] = useState(null);
  const navigate = useNavigate();

  // Both catalogue pages can be open in different tabs, and each writes the same
  // key, so follow the store rather than local state alone.
  useEffect(() => {
    const sync = () => setItems(readPanelBasket());
    window.addEventListener("panelBasketUpdated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("panelBasketUpdated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addItem = useCallback((item) => {
    // An ended free trial cannot buy single items (the API refuses them): send it to the plans.
    if (catalogueLocked) {
      toast.info("Your free trial has ended. Choose a plan to continue.");
      navigate(`/${process.env.REACT_APP_STUDENT_PREFIX || "student"}/choose-plan?plans=1`);
      return;
    }
    if (canToggleDashboard && dashboardView === "course" && PLAN_COVERED_TYPES.includes(item?.resourceType)) {
      setPlanNotice(item);
      return;
    }
    setItems(addToPanelBasket(item));
  }, [canToggleDashboard, dashboardView, catalogueLocked, navigate]);

  const removeItem = useCallback((item) => {
    setItems(removeFromPanelBasket(item));
  }, []);

  const clear = useCallback(() => {
    setItems(clearPanelBasket());
  }, []);

  const closeDialog = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setCheckout(null);
    setError("");
    setParentConfirmed(false);
    setSupplyConsent(false);
  }, [busy]);

  const startPayment = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const { clientSecret, publishableKey } = await createPanelBasketCheckout(items, { immediateSupplyConsent: supplyConsent });
      // loadStripe on a click, not at module load: the panel should not fetch
      // Stripe's script for students who never buy anything.
      setCheckout({ clientSecret, stripePromise: loadStripe(publishableKey) });
    } catch (err) {
      setError(err?.message || "Could not start payment.");

      // The server refuses the whole order over one bad row, so leaving it in place
      // meant every retry failed the same way. Drop just that row and let the rest
      // stay payable — the message above still names what happened.
      const refusal = err?.refusal;
      const blocked = refusal?.already_purchased || refusal?.already_included;
      if (blocked && refusal?.resource_type && refusal?.resource_id != null) {
        setItems(
          removeFromPanelBasket({
            resourceType: refusal.resource_type,
            resourceId: refusal.resource_id,
          })
        );
      }
    } finally {
      setBusy(false);
    }
  }, [items, supplyConsent]);

  const total = useMemo(() => panelBasketTotal(items), [items]);
  const currency = useMemo(() => panelBasketCurrency(items), [items]);
  const paying = Boolean(checkout);

  const basketUi = (
    <>
      <Slide direction="up" in={items.length > 0} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            left: { xs: 8, md: 24 },
            right: { xs: 8, md: 24 },
            bottom: { xs: 8, md: 16 },
            zIndex: 1200,
            borderRadius: 2,
            px: { xs: 1.5, md: 2.5 },
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            background: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
            color: "#fff",
          }}
        >
          <ShoppingCartIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem" }}>
            {items.length} {items.length === 1 ? "item" : "items"}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", opacity: 0.85 }}>
            {currency}
            {total.toFixed(2)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={clear}
            sx={{ color: "rgba(255,255,255,0.75)", textTransform: "none" }}
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              background: "linear-gradient(90deg, #7c3aed, #6d28d9)",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              "&:hover": { opacity: 0.9 },
            }}
          >
            Review &amp; pay
          </Button>
        </Paper>
      </Slide>

      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth={paying ? "sm" : "xs"}
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {paying ? "Payment" : "Confirm purchase"}
        </DialogTitle>
        <DialogContent>
          {paying ? (
            <>
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.10)",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                <EmbeddedCheckoutProvider
                  stripe={checkout.stripePromise}
                  options={{ clientSecret: checkout.clientSecret }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                <LockOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  Card details go straight to Stripe — they never reach our servers.
                </Typography>
              </Stack>
            </>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                You are about to buy:
              </Typography>

              <Stack spacing={0.75}>
                {items.map((item) => (
                  <Stack
                    key={`${item.resourceType}:${item.resourceId}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.9rem" }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                      {item.priceDisplay || `${currency}${Number(item.price || 0).toFixed(2)}`}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${item.name}`}
                      onClick={() => removeItem(item)}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>

              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                <Typography sx={{ fontWeight: 700, color: "#4c1d95", fontSize: "1.1rem" }}>
                  {currency}
                  {total.toFixed(2)}
                </Typography>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Payment happens on this page, in Stripe's secure form. Nothing is charged
                until you enter a card.
              </Typography>
              <FormControlLabel
                sx={{ alignItems: "flex-start", mr: 0 }}
                control={
                  <Checkbox
                    checked={parentConfirmed}
                    onChange={(e) => setParentConfirmed(e.target.checked)}
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm I am the parent or guardian authorising this purchase.
                  </Typography>
                }
              />
              <ImmediateSupplyConsentCheckbox checked={supplyConsent} onChange={setSupplyConsent} />
              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDialog} disabled={busy} sx={{ textTransform: "none" }}>
            {paying ? "Back" : "Cancel"}
          </Button>
          {paying ? null : (
            <Button
              variant="contained"
              onClick={startPayment}
              disabled={busy || !parentConfirmed || !supplyConsent || items.length === 0}
              startIcon={<ShoppingCartIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {busy ? "Opening payment…" : "Continue to payment"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(planNotice)} onClose={() => setPlanNotice(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Already in your mobile plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{planNotice?.name}</strong> is already included in your mobile plan, so there is
            nothing to buy. Switch to Mobile plan to start it now.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPlanNotice(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setPlanNotice(null);
              setDashboardView("mobile");
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Switch to Mobile plan
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { items, addItem, removeItem, clear, basketUi };
}

export default usePanelBasket;
